import {
  type MutateOptions,
  type MutationFunctionContext,
  type UseMutateAsyncFunction,
  type UseMutateFunction,
  type UseMutationResult,
  type UseMutationOptions as useMutationOptionsReact,
  useMutation as useMutationReact,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services/api';
// TODO: break circular dependency
import { handleAxiosError } from 'ui/axios-error-handler';

const defaultMutateOnError = <TError extends Error = ApiClientResponseError<ResponseErrorDataObject>>(
  error: TError,
) => {
  handleAxiosError(error);
};

export type UseMutationOptions<
  TData,
  TVariables,
  TError extends Error = ApiClientResponseError<ResponseErrorDataObject>,
  TOnMutateResult = unknown,
> = {
  defaultMutateOnError?: MutateOptions<TData, TError, TVariables, TOnMutateResult>['onError'];
} & useMutationOptionsReact<TData, TError, TVariables, TOnMutateResult>;

export const useMutation = <
  TData,
  TVariables,
  TError extends Error = ApiClientResponseError<ResponseErrorDataObject>,
  TOnMutateResult = unknown,
>({
  defaultMutateOnError: defaultMutateOnErrorHandler = defaultMutateOnError,
  ...options
}: UseMutationOptions<TData, TVariables, TError, TOnMutateResult>): UseMutationResult<
  TData,
  TError,
  TVariables,
  TOnMutateResult
> => {
  const {
    mutate: mutateReactQuery,
    mutateAsync: mutateAsyncReactQuery,
    ...rest
  } = useMutationReact<TData, TError, TVariables, TOnMutateResult>(options);

  const callbackOnError = useCallback(
    (
      error: TError,
      variables: TVariables,
      onMutateResult: TOnMutateResult | undefined,
      context: MutationFunctionContext,
    ) => {
      defaultMutateOnErrorHandler(error, variables, onMutateResult, context);
    },
    [defaultMutateOnErrorHandler],
  );

  const mutate = useCallback(
    (...mutateArguments: Parameters<UseMutateFunction<TData, TError, TVariables, TOnMutateResult>>) => {
      const mutateOptions = mutateArguments[1];
      mutateArguments[1] = {
        ...mutateOptions,
        onError: mutateOptions?.onError ?? callbackOnError,
      };
      mutateReactQuery(...mutateArguments);
    },
    [callbackOnError, mutateReactQuery],
  );

  const mutateAsync = useCallback(
    async (...mutateArguments: Parameters<UseMutateAsyncFunction<TData, TError, TVariables, TOnMutateResult>>) => {
      const mutateOptions = mutateArguments[1];
      mutateArguments[1] = {
        ...mutateOptions,
        onError: mutateOptions?.onError ?? callbackOnError,
      };
      return mutateAsyncReactQuery(...mutateArguments);
    },
    [callbackOnError, mutateAsyncReactQuery],
  );

  return {
    mutate,
    mutateAsync,
    ...rest,
  };
};
